import React, { useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { actions as CredentialActions } from '../../Ducks/Credentials.duck';
import { FormattedMessage } from 'react-intl';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { DivRootStyled } from './OauthCredentialResponsePage.styles';

const normalizeValue = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const resolveStatus = (status, searchParams) => {
  const normalizedStatus = normalizeValue(status);
  const normalizedInvalidType = normalizeValue(searchParams.get('invalidType'));
  const normalizedReason = normalizeValue(searchParams.get('reason'));

  const hasInvalidStateReason =
    normalizedInvalidType === 'invalid_state' ||
    normalizedReason === 'invalid_state' ||
    normalizedReason.includes('invalid_state');

  if (hasInvalidStateReason) {
    return 'invalid_state';
  }

  return normalizedStatus || status;
};

const OauthCredentialResponsePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { credentialId, status } = useParams();
  const [searchParams] = useSearchParams();

  const resolvedStatus = useMemo(() => resolveStatus(status, searchParams), [status, searchParams]);
  const normalizedResolvedStatus = useMemo(() => normalizeValue(resolvedStatus), [resolvedStatus]);
  const isError = Boolean(normalizedResolvedStatus && normalizedResolvedStatus !== 'success');

  useEffect(() => {
    if (credentialId && resolvedStatus) {
      dispatch(CredentialActions.oauthStatus({ [credentialId]: resolvedStatus }));
    }
  }, [dispatch, credentialId, resolvedStatus]);

  if (!resolvedStatus) {
    return null;
  }

  const handleClose = () => {
    navigate('/credential/requests');
  };

  if (isError) {
    return (
      <Dialog open={true} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon color="error" />
            <Typography variant="h6">
              <FormattedMessage id="credential.oauth.failedTitle" />
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" paragraph>
              <FormattedMessage id="credential.oauth.failedMessage" />
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              <FormattedMessage id="credential.oauth.retryInstruction" />
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} variant="contained" color="primary">
            <FormattedMessage id="button.proceed" />
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <DivRootStyled>
      <FormattedMessage id={`credential.oauth.${resolvedStatus}`} />
    </DivRootStyled>
  );
};

export default OauthCredentialResponsePage;
